import { AdditiveBlending, BufferAttribute, BufferGeometry, Color, Group, InstancedMesh, Matrix4, MeshBasicMaterial, Points, ShaderMaterial, SphereGeometry, Vector3 } from 'three';
import { SeededRandom } from '../core/SeededRandom';
import type { ResolvedQuality } from '../types';

const vertex = /* glsl */`
attribute vec3 aColor; attribute float aRadius; attribute float aBrightness;
uniform float uIntensity; uniform float uFocal; uniform float uRatio; uniform float uVisibility;
varying vec3 vColor; varying float vBright;
void main() {
  vec4 view = modelViewMatrix * vec4(position, 1.);
  gl_Position = projectionMatrix * view;
  float apparent = aRadius * 2. * uFocal / max(.1, -view.z);
  gl_PointSize = clamp(max((.65 + aBrightness) * uRatio, apparent * 3.), .7, 80.);
  vBright = aBrightness;
  vColor = aColor * uIntensity * (.5 + aBrightness * .55) * min(1., uVisibility / max(1., length(view.xyz)));
}
`;
const fragment = /* glsl */`
varying vec3 vColor; varying float vBright;
void main() {
  float d = length(gl_PointCoord - .5);
  if (d > .5) discard;
  vec2 p = abs(gl_PointCoord - .5);
  float spikes = exp(-min(p.x, p.y) * 180.) * exp(-max(p.x, p.y) * 12.) * smoothstep(2., 4., vBright) * .22;
  float glow = exp(-d * d * 90.) + exp(-d * 9.) * .16 + spikes;
  gl_FragColor = vec4(vColor * glow, 1.);
}
`;

const CELL = 384, NEAR = 220, CAPACITY = 256;
export interface StarSample { position: Vector3; radius: number; color: Color }

/** Fixed stars: far points and nearby solid spheres share the exact same data. */
export class StellarField {
  readonly root = new Group();
  readonly points: Points<BufferGeometry, ShaderMaterial>;
  readonly cores: InstancedMesh<SphereGeometry, MeshBasicMaterial>;
  private cells = new Map<string, number[]>();
  private localEye = new Vector3();
  private nearCount = 0;
  private matrix = new Matrix4();
  private quality: ResolvedQuality = 'high';
  constructor(count: number, seed: number, point: (random: SeededRandom, index: number) => Vector3, radiusScale = 1, visibility = 1600) {
    const random = new SeededRandom(seed), positions = new Float32Array(count * 3), colors = new Float32Array(count * 3);
    const radii = new Float32Array(count), brightness = new Float32Array(count);
    const palette = ['#d4e2ff', '#f7e9d0', '#82b5ed', '#e5b79a', '#ffffff'].map(value => new Color(value));
    for (let i = 0; i < count; i++) {
      const position = point(random, i); position.toArray(positions, i * 3);
      palette[random.int(0, palette.length - 1)].toArray(colors, i * 3);
      radii[i] = random.range(.65, 2.6) * radiusScale;
      brightness[i] = random.next() > .993 ? random.range(3, 5) : random.range(.15, 1.4);
      const key = this.cellKey(position.x, position.y, position.z);
      const cell = this.cells.get(key) ?? []; cell.push(i); this.cells.set(key, cell);
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3)); geometry.setAttribute('aColor', new BufferAttribute(colors, 3));
    geometry.setAttribute('aRadius', new BufferAttribute(radii, 1)); geometry.setAttribute('aBrightness', new BufferAttribute(brightness, 1));
    this.points = new Points(geometry, new ShaderMaterial({ vertexShader: vertex, fragmentShader: fragment, transparent: true,
      blending: AdditiveBlending, depthWrite: false, uniforms: { uIntensity: { value: 1 }, uFocal: { value: 800 }, uRatio: { value: 1 }, uVisibility: { value: visibility } } }));
    this.points.renderOrder = -10;
    this.cores = new InstancedMesh(new SphereGeometry(1, 16, 12), new MeshBasicMaterial({ color: new Color(2, 2, 2) }), CAPACITY);
    this.cores.count = 0; this.cores.frustumCulled = false;
    this.root.add(this.points, this.cores);
  }
  private cellKey(x: number, y: number, z: number) { return `${Math.floor(x / CELL)},${Math.floor(y / CELL)},${Math.floor(z / CELL)}`; }
  sample(index: number): StarSample {
    return { position: new Vector3().fromBufferAttribute(this.points.geometry.getAttribute('position'), index),
      radius: this.points.geometry.getAttribute('aRadius').getX(index), color: new Color().fromBufferAttribute(this.points.geometry.getAttribute('aColor'), index) };
  }
  setQuality(quality: ResolvedQuality) { this.quality = quality; }
  update(eye: Vector3, height: number, fov: number, pixelRatio: number) {
    this.root.updateWorldMatrix(true, false);
    this.localEye.copy(eye); this.root.worldToLocal(this.localEye);
    this.points.material.uniforms.uFocal.value = height * pixelRatio / (2 * Math.tan(fov * Math.PI / 360));
    this.points.material.uniforms.uRatio.value = pixelRatio;
    const candidates: Array<{ index: number; distance: number }> = [];
    const positions = this.points.geometry.getAttribute('position');
    for (let x = Math.floor((this.localEye.x - NEAR) / CELL); x <= Math.floor((this.localEye.x + NEAR) / CELL); x++)
      for (let y = Math.floor((this.localEye.y - NEAR) / CELL); y <= Math.floor((this.localEye.y + NEAR) / CELL); y++)
        for (let z = Math.floor((this.localEye.z - NEAR) / CELL); z <= Math.floor((this.localEye.z + NEAR) / CELL); z++)
          for (const index of this.cells.get(`${x},${y},${z}`) ?? []) {
            const distance = (positions.getX(index) - this.localEye.x) ** 2 + (positions.getY(index) - this.localEye.y) ** 2 + (positions.getZ(index) - this.localEye.z) ** 2;
            if (distance < NEAR * NEAR) candidates.push({ index, distance });
          }
    candidates.sort((a, b) => a.distance - b.distance);
    // Quality changes geometric detail only; it never removes a star from the world.
    this.nearCount = Math.min(candidates.length, this.quality === 'low' ? 64 : CAPACITY);
    for (let i = 0; i < this.nearCount; i++) {
      const star = this.sample(candidates[i].index);
      this.matrix.makeScale(star.radius, star.radius, star.radius).setPosition(star.position);
      this.cores.setMatrixAt(i, this.matrix); this.cores.setColorAt(i, star.color);
    }
    this.cores.count = this.nearCount; this.cores.instanceMatrix.needsUpdate = true;
    if (this.cores.instanceColor) this.cores.instanceColor.needsUpdate = true;
  }
  get stats() { return { fixedStars: this.points.geometry.getAttribute('position').count, nearbyStarCores: this.nearCount }; }
}

/** A finite stellar neighbourhood, with a denser tilted band and open space beyond. */
export function deepStarPosition(random: SeededRandom) {
  const azimuth = random.range(0, Math.PI * 2), latitude = random.next() < .55 ? random.signed(.2) : random.signed();
  const radius = random.range(6500, 42000), ring = Math.sqrt(1 - latitude * latitude);
  const point = new Vector3(Math.cos(azimuth) * ring, latitude, Math.sin(azimuth) * ring).multiplyScalar(radius);
  return point.applyAxisAngle(new Vector3(0, 0, 1), -.38);
}
